import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  ChevronRight, 
  ChevronLeft, 
  Building2, 
  Target, 
  Server,
  FileText,
  Sparkles,
  Zap
} from 'lucide-react';
import { generateDocumentsWithAI } from '../services/openaiService';
import GenerationProgress from './GenerationProgress';
import DocumentViewer from './DocumentViewer';

interface BesoinsWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

interface GeneratedDocuments {
  cahierDeCharge: string;
  roadmap: string;
  planDirecteur?: string;
  ods?: string;
}

// Données mockup pour tests rapides - PME 50 employés (TOUTES LES 4 PHASES COMPLÈTES)
const MOCK_DATA = {
  // PHASE 1: Qualification Client
  company_name: "TechnoSolutions Inc.",
  company_type: "PME établie",
  industry: "Cabinet comptable / CPA",
  employees: "50-100 employés",
  revenue: "3M$ - 10M$",
  it_maturity: "Intermédiaire - Systèmes en place, mais peu optimisés",
  it_team: "1-2 personnes",
  
  // PHASE 2: Contexte & Objectifs
  main_challenge: "Notre infrastructure TI vieillissante (8+ ans) génère des pannes fréquentes et ralentit nos opérations. Nos équipes perdent en moyenne 2-3h/semaine à cause de problèmes techniques. De plus, nos systèmes ne communiquent pas entre eux (données en silos), ce qui complexifie la gestion et nuit à notre croissance.",
  business_goals: [
    "Croissance des revenus",
    "Amélioration de l'efficacité opérationnelle",
    "Transformation numérique",
    "Amélioration de l'expérience client"
  ],
  it_goals: [
    "Moderniser l'infrastructure",
    "Améliorer la sécurité",
    "Migration cloud",
    "Automatisation des processus",
    "Réduire les temps d'arrêt"
  ],
  timeline: "Moyen terme (6-12 mois)",
  budget_range: "100K$ - 250K$",
  success_metrics: "Nous mesurerons le succès par: 1) Réduction de 80% des pannes système, 2) Disponibilité des systèmes à 99.5%+, 3) Gain de productivité de 25% (moins de temps perdu), 4) Satisfaction employés augmentée de 40%, 5) Conformité Loi 25 à 100%, 6) ROI positif dans les 18 mois.",
  
  // PHASE 3: Besoins Techniques
  current_systems: [
    "ERP (SAP, Oracle, Dynamics, etc.)",
    "CRM (Salesforce, HubSpot, etc.)",
    "Comptabilité / Finance",
    "Business Intelligence / Analytics"
  ],
  infrastructure: [
    "Serveurs on-premise",
    "Hybride (on-premise + cloud)"
  ],
  pain_points: [
    "Pannes fréquentes",
    "Performance lente",
    "Technologie obsolète",
    "Manque d'intégration entre systèmes",
    "Processus manuels trop nombreux",
    "Sécurité insuffisante"
  ],
  priorities: [
    "Sécurité & Conformité",
    "Performance & Disponibilité",
    "Agilité & Flexibilité"
  ],
  security_concerns: [
    "Protection des données clients",
    "Conformité réglementaire (RGPD, HIPAA, etc.)",
    "Cyberattaques / Ransomware",
    "Backup & Disaster Recovery",
    "Formation des employés à la sécurité"
  ],
  integration_needs: "Nous avons besoin d'intégrer notre ERP avec notre CRM pour avoir une vue unifiée des clients. Également, notre système comptable doit se synchroniser automatiquement avec l'ERP pour éliminer la double saisie. Enfin, nous voulons un dashboard BI qui agrège les données de tous ces systèmes en temps réel.",
  users_impacted: "51-100 utilisateurs",
  
  // PHASE 4: Gouvernance & Support
  decision_makers: "PDG (Sophie Tremblay), Directrice des Opérations (Marc Gagnon), Directeur Financier/CFO (Jean Lapointe), Responsable TI (Daniel Roy). La décision finale appartient au PDG avec recommandation du CFO pour le budget.",
  stakeholders: [
    "Direction générale (C-Suite)",
    "Finances / Comptabilité",
    "Opérations / Production",
    "Tous les départements"
  ],
  change_readiness: "Ouverte - bonne volonté générale",
  training_needs: "Formation complète - programme structuré",
  support_model: "Support hybride - mix des approches",
  additional_info: "Contexte important: nous avons vécu un incident critique il y a 6 mois (panne serveur 48h, perte partielle de données, arrêt des opérations). Cet événement a été un électrochoc pour notre direction qui comprend maintenant l'importance d'investir dans les TI. Nous cherchons un partenaire stratégique de confiance qui nous guidera dans la modernisation, pas juste un fournisseur transactionnel. Nous voulons une relation à long terme avec BZ."
};

const QUESTIONS = {
  phase1: {
    title: "Qualification Client",
    icon: Building2,
    questions: [
      {
        id: "company_name",
        question: "Nom de l'entreprise",
        type: "text",
        required: true
      },
      {
        id: "company_type",
        question: "Type d'entreprise",
        type: "select",
        options: ["Startup / Entreprise en démarrage", "PME établie", "Entreprise en croissance", "Organisation"],
        required: true
      },
      {
        id: "industry",
        question: "Industrie / Secteur d'activité",
        type: "select",
        options: [
          "Cabinet d'avocats / Juridique",
          "Cabinet comptable / CPA",
          "Services professionnels (Autre)",
          "Manufacturing / Production",
          "Commerce de détail",
          "Santé",
          "Finance / Assurance",
          "Technologies",
          "Construction",
          "Transport / Logistique",
          "Éducation",
          "Autre"
        ],
        required: true
      },
      {
        id: "employees",
        question: "Nombre d'employés",
        type: "select",
        options: ["Moins de 25 employés", "25-49 employés", "50-100 employés", "100-250 employés", "250 employés et plus"],
        required: true
      },
      {
        id: "revenue",
        question: "Chiffre d'affaires annuel (approximatif)",
        type: "select",
        options: ["Moins de 1M$", "1M$ - 3M$", "3M$ - 10M$", "10M$ - 25M$", "25M$ - 50M$", "50M$ et plus"],
        required: false
      },
      {
        id: "it_maturity",
        question: "Maturité TI actuelle",
        type: "select",
        options: [
          "Débutant - Infrastructure basique, peu de systèmes",
          "Intermédiaire - Systèmes en place, mais peu optimisés",
          "Avancé - Infrastructure solide, processus établis",
          "Expert - TI stratégique, innovation continue"
        ],
        required: true
      },
      {
        id: "it_team",
        question: "Équipe TI actuelle",
        type: "select",
        options: [
          "Aucune équipe dédiée",
          "1-2 personnes",
          "3-5 personnes",
          "6-10 personnes",
          "10+ personnes",
          "Totalement externalisé"
        ],
        required: true
      }
    ]
  },
  phase2: {
    title: "Contexte & Objectifs",
    icon: Target,
    questions: [
      {
        id: "main_challenge",
        question: "Quel est votre principal défi TI actuellement ?",
        type: "textarea",
        placeholder: "Ex: Notre infrastructure est obsolète et cause des interruptions fréquentes...",
        required: true
      },
      {
        id: "business_goals",
        question: "Objectifs d'affaires pour les 12 prochains mois",
        type: "multiselect",
        options: [
          "Croissance des revenus",
          "Amélioration de l'efficacité opérationnelle",
          "Expansion géographique",
          "Nouveaux produits/services",
          "Transformation numérique",
          "Réduction des coûts",
          "Amélioration de l'expérience client",
          "Conformité réglementaire"
        ],
        required: true
      },
      {
        id: "it_goals",
        question: "Quels sont vos objectifs TI prioritaires ?",
        type: "multiselect",
        options: [
          "Moderniser l'infrastructure",
          "Améliorer la sécurité",
          "Migration cloud",
          "Automatisation des processus",
          "Business Intelligence / Analytics",
          "Mobilité des employés",
          "Collaboration à distance",
          "Innovation / IA",
          "Réduire les temps d'arrêt",
          "Optimiser les coûts TI"
        ],
        required: true
      },
      {
        id: "timeline",
        question: "Quel est votre horizon temporel ?",
        type: "select",
        options: [
          "Urgent (0-3 mois)",
          "Court terme (3-6 mois)",
          "Moyen terme (6-12 mois)",
          "Long terme (1-2 ans)",
          "Stratégique (2-5 ans)"
        ],
        required: true
      },
      {
        id: "budget_range",
        question: "Budget estimé pour ce projet",
        type: "select",
        options: [
          "Moins de 25K$",
          "25K$ - 50K$",
          "50K$ - 100K$",
          "100K$ - 250K$",
          "250K$ - 500K$",
          "500K$ et plus",
          "À déterminer"
        ],
        required: false
      },
      {
        id: "success_metrics",
        question: "Comment mesurerez-vous le succès de ce projet ?",
        type: "textarea",
        placeholder: "Ex: Réduction de 50% des temps d'arrêt, augmentation de 30% de la productivité...",
        required: true
      }
    ]
  },
  phase3: {
    title: "Besoins Techniques",
    icon: Server,
    questions: [
      {
        id: "current_systems",
        question: "Systèmes critiques actuels",
        type: "multiselect",
        options: [
          "ERP (SAP, Oracle, Dynamics, etc.)",
          "CRM (Salesforce, HubSpot, etc.)",
          "Comptabilité / Finance",
          "Gestion de projet",
          "RH / Paie",
          "E-commerce",
          "Inventory / Supply Chain",
          "Manufacturing Execution System (MES)",
          "Business Intelligence / Analytics",
          "Autre"
        ],
        required: true
      },
      {
        id: "infrastructure",
        question: "Infrastructure actuelle",
        type: "multiselect",
        options: [
          "Serveurs on-premise",
          "Cloud public (AWS, Azure, GCP)",
          "Cloud privé",
          "Hybride (on-premise + cloud)",
          "Hébergement tiers",
          "SaaS uniquement"
        ],
        required: true
      },
      {
        id: "pain_points",
        question: "Points de douleur principaux",
        type: "multiselect",
        options: [
          "Performance lente",
          "Pannes fréquentes",
          "Sécurité insuffisante",
          "Manque d'intégration entre systèmes",
          "Processus manuels trop nombreux",
          "Difficulté à obtenir des rapports",
          "Support utilisateur insuffisant",
          "Coûts TI trop élevés",
          "Technologie obsolète",
          "Manque de visibilité sur les opérations"
        ],
        required: true
      },
      {
        id: "priorities",
        question: "Classez vos priorités (sélectionnez top 3)",
        type: "multiselect",
        maxSelections: 3,
        options: [
          "Sécurité & Conformité",
          "Performance & Disponibilité",
          "Innovation & Compétitivité",
          "Réduction des coûts",
          "Expérience utilisateur",
          "Scalabilité / Croissance",
          "Agilité & Flexibilité"
        ],
        required: true
      },
      {
        id: "security_concerns",
        question: "Préoccupations de sécurité",
        type: "multiselect",
        options: [
          "Protection des données clients",
          "Conformité réglementaire (RGPD, HIPAA, etc.)",
          "Cyberattaques / Ransomware",
          "Accès non autorisés",
          "Perte de données",
          "Backup & Disaster Recovery",
          "Formation des employés à la sécurité",
          "Aucune préoccupation majeure"
        ],
        required: true
      },
      {
        id: "integration_needs",
        question: "Besoins d'intégration",
        type: "textarea",
        placeholder: "Décrivez les systèmes qui doivent communiquer ensemble...",
        required: false
      },
      {
        id: "users_impacted",
        question: "Nombre d'utilisateurs impactés par ce projet",
        type: "select",
        options: ["1-10 utilisateurs", "11-25 utilisateurs", "26-50 utilisateurs", "51-100 utilisateurs", "100+ utilisateurs", "Toute l'entreprise"],
        required: true
      }
    ]
  },
  phase4: {
    title: "Gouvernance & Support",
    icon: FileText,
    questions: [
      {
        id: "decision_makers",
        question: "Qui sont les décideurs clés pour ce projet ?",
        type: "textarea",
        placeholder: "Ex: PDG, Directeur des opérations, CFO...",
        required: true
      },
      {
        id: "stakeholders",
        question: "Parties prenantes principales",
        type: "multiselect",
        options: [
          "Direction générale (C-Suite)",
          "Finances / Comptabilité",
          "Opérations / Production",
          "Ventes / Marketing",
          "RH",
          "Service client",
          "Tous les départements"
        ],
        required: true
      },
      {
        id: "change_readiness",
        question: "L'organisation est-elle prête au changement ?",
        type: "select",
        options: [
          "Très réticente - beaucoup de résistance attendue",
          "Modérément réticente - un peu de résistance",
          "Neutre - acceptation passive",
          "Ouverte - bonne volonté générale",
          "Très enthousiaste - demande active de changement"
        ],
        required: true
      },
      {
        id: "training_needs",
        question: "Besoins de formation",
        type: "select",
        options: [
          "Formation minimale - utilisateurs autonomes",
          "Formation de base - quelques sessions",
          "Formation complète - programme structuré",
          "Formation continue - support à long terme"
        ],
        required: true
      },
      {
        id: "support_model",
        question: "Modèle de support souhaité",
        type: "select",
        options: [
          "Support réactif - intervention sur demande",
          "Support proactif - surveillance et prévention",
          "Support dédié - ressource à temps plein",
          "Support hybride - mix des approches",
          "Support interne - notre équipe gère"
        ],
        required: true
      },
      {
        id: "additional_info",
        question: "Informations additionnelles ou préoccupations",
        type: "textarea",
        placeholder: "Tout ce que nous devrions savoir d'autre...",
        required: false
      }
    ]
  }
};

export default function BesoinsWizard({ isOpen, onClose }: BesoinsWizardProps) {
  const [currentPhase, setCurrentPhase] = useState<keyof typeof QUESTIONS>('phase1');
  const [responses, setResponses] = useState<Record<string, string | string[]>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState('');
  const [generatedDocs, setGeneratedDocs] = useState<GeneratedDocuments | null>(null);
  
  const phases = Object.keys(QUESTIONS) as Array<keyof typeof QUESTIONS>;
  const currentPhaseIndex = phases.indexOf(currentPhase);
  const currentPhaseData = QUESTIONS[currentPhase];
  const progress = ((currentPhaseIndex + 1) / phases.length) * 100;

  const handleResponse = (questionId: string, value: string | string[]) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const isPhaseComplete = () => {
    const requiredQuestions = currentPhaseData.questions.filter(q => q.required);
    return requiredQuestions.every(q => {
      const response = responses[q.id];
      if (Array.isArray(response)) {
        return response.length > 0;
      }
      return response && response.trim() !== '';
    });
  };

  const handleNext = () => {
    if (currentPhaseIndex < phases.length - 1) {
      setCurrentPhase(phases[currentPhaseIndex + 1]);
    } else {
      handleGenerate();
    }
  };

  const handleBack = () => {
    if (currentPhaseIndex > 0) {
      setCurrentPhase(phases[currentPhaseIndex - 1]);
    }
  };

  const fillMockData = () => {
    setResponses(MOCK_DATA);
    alert('✅ Formulaire rempli avec les données de test (PME 50 employés)!\n\nVous pouvez maintenant naviguer à travers les phases et générer les documents.');
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setGenerationProgress(0);
    
    try {
      // Lancer la génération en arrière-plan
      const docsPromise = generateDocumentsWithAI({ responses });

      // Simulation de progression RÉALISTE pendant la génération
      const steps = [
        { progress: 5, text: 'Analyse de vos réponses...', delay: 1000 },
        { progress: 12, text: 'Structuration des données...', delay: 1500 },
        { progress: 20, text: 'Génération des Spécifications d\'exigences...', delay: 3000 },
        { progress: 35, text: 'Analyse des exigences techniques...', delay: 2500 },
        { progress: 50, text: 'Création du Roadmap annuel...', delay: 3000 },
        { progress: 65, text: 'Planification des jalons...', delay: 2500 },
        { progress: 78, text: 'Élaboration du Plan Directeur...', delay: 3500 },
        { progress: 88, text: 'Finalisation des documents...', delay: 2000 },
        { progress: 93, text: 'Mise en forme finale...', delay: 2000 },
        { progress: 97, text: 'Vérification qualité...', delay: 1500 },
      ];

      // Animer la progression de manière réaliste
      let stepIndex = 0;
      const progressInterval = setInterval(() => {
        if (stepIndex < steps.length) {
          const step = steps[stepIndex];
          setGenerationStep(step.text);
          setGenerationProgress(step.progress);
          stepIndex++;
        }
      }, 2200); // Moyenne de 2.2 secondes par étape

      // Attendre que la génération soit terminée
      const docs = await docsPromise;
      
      clearInterval(progressInterval);
      setGenerationProgress(100);
      setGenerationStep('Génération terminée !');
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setGeneratedDocs(docs);
    } catch (error) {
      console.error('Erreur lors de la génération:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      alert(`❌ Erreur lors de la génération:\n\n${errorMessage}\n\nVérifiez la console (F12) pour plus de détails.`);
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  };

  const renderQuestion = (question: any) => {
    const value = responses[question.id] || '';

    switch (question.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value as string}
            onChange={(e) => handleResponse(question.id, e.target.value)}
            placeholder={question.placeholder}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bz-blue focus:border-transparent"
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value as string}
            onChange={(e) => handleResponse(question.id, e.target.value)}
            placeholder={question.placeholder}
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bz-blue focus:border-transparent resize-none"
          />
        );

      case 'select':
        return (
          <select
            value={value as string}
            onChange={(e) => handleResponse(question.id, e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bz-blue focus:border-transparent bg-white"
          >
            <option value="">-- Sélectionnez --</option>
            {question.options.map((opt: string) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'multiselect':
        return (
          <div className="space-y-2">
            {question.options.map((opt: string) => {
              const selected = Array.isArray(value) && value.includes(opt);
              const canSelect = !question.maxSelections || 
                              !Array.isArray(value) || 
                              value.length < question.maxSelections || 
                              selected;
              
              return (
                <label
                  key={opt}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                    selected 
                      ? 'border-bz-blue bg-bz-blue/5' 
                      : canSelect 
                        ? 'border-gray-300 hover:border-bz-blue/50 hover:bg-gray-50'
                        : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    disabled={!canSelect}
                    onChange={(e) => {
                      const currentValues = Array.isArray(value) ? value : [];
                      const newValues = e.target.checked
                        ? [...currentValues, opt]
                        : currentValues.filter(v => v !== opt);
                      handleResponse(question.id, newValues);
                    }}
                    className="mt-1"
                  />
                  <span className="text-gray-700">{opt}</span>
                </label>
              );
            })}
            {question.maxSelections && (
              <p className="text-sm text-gray-500 italic">
                Sélectionnez jusqu'à {question.maxSelections} options
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  // Afficher l'écran de génération
  if (isGenerating && !generatedDocs) {
    return <GenerationProgress currentStep={generationStep} progress={generationProgress} />;
  }

  // Afficher le viewer de documents
  if (generatedDocs) {
    return (
      <DocumentViewer
        documents={generatedDocs}
        companyName={responses.company_name as string || 'Votre entreprise'}
        timeline={responses.timeline as string || 'Moyen terme'}
        onClose={() => {
          setGeneratedDocs(null);
          setIsGenerating(false);
          onClose();
        }}
      />
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-bz-dark to-bz-blue text-white p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-2xl font-bold mb-2">Assistant de Prise de Besoin</h2>
                <p className="text-white/90">Répondez aux questions pour générer vos livrables</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={fillMockData}
                  className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-4 py-2 rounded-lg font-semibold transition-colors shadow-lg"
                  title="Remplir automatiquement avec des données de test"
                >
                  <Zap className="w-5 h-5" />
                  Test Auto
                </button>
                <button
                  onClick={onClose}
                  className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Progress bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{currentPhaseData.title}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="bg-white/20 rounded-full h-2 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  className="bg-white h-2"
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentPhase}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {currentPhaseData.questions.map((question, idx) => (
                  <div key={question.id} className="space-y-2">
                    <label className="block font-medium text-gray-900">
                      {idx + 1}. {question.question}
                      {question.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {renderQuestion(question)}
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t">
            <button
              onClick={handleBack}
              disabled={currentPhaseIndex === 0}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
              Précédent
            </button>

            <button
              onClick={handleNext}
              disabled={!isPhaseComplete()}
              className="flex items-center gap-2 px-6 py-2 bg-bz-blue text-white rounded-lg hover:bg-bz-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currentPhaseIndex === phases.length - 1 ? (
                <>
                  <Sparkles className="w-5 h-5" />
                  Générer les documents
                </>
              ) : (
                <>
                  Suivant
                  <ChevronRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

